// FILE: src/services/sessionReviewService.js
//
// Loads ONE completed attempt for the reopenable review route
// (?view=results&session=<uuid>).
//
// Security: the read goes through the get_session_review RPC (migration 097),
// which is owner-only — row-level security plus an explicit
// `user_id = auth.uid()` predicate, so an admin's broader DB access does not
// reach another learner's review, and guest sessions are never reviewable.
// Not-found, not-owned and signed-out are indistinguishable by design: the RPC
// returns NULL for all three and this service reports 'not_found'.
import { supabase } from './supabaseClient';
import { trackEvent, trackTiming } from './telemetryService';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isSessionId(value) {
  return typeof value === 'string' && UUID_RE.test(value.trim());
}

/**
 * @param {string} sessionId
 * @returns {Promise<{status:'ok'|'not_found'|'error', payload?:object, error?:string}>}
 */
export async function loadSessionReview(sessionId) {
  if (!isSessionId(sessionId)) return { status: 'not_found' };
  const t0 = Date.now();
  try {
    const { data, error } = await supabase.rpc('get_session_review', {
      p_session_id: sessionId.trim(),
    });
    if (error) {
      console.warn('[sessionReviewService] load failed:', error.message);
      trackEvent('failed_load', 'technical', {
        target: 'session_review', code: 'supabase_error',
        message_short: String(error.message || '').slice(0, 80),
      });
      return { status: 'error', error: error.message };
    }
    if (!data || !data.session) return { status: 'not_found' };
    trackTiming('session_review_load', Date.now() - t0, {
      exam_id: data.session.exam_id,
      question_count: Array.isArray(data.items) ? data.items.length : 0,
    });
    return { status: 'ok', payload: data };
  } catch (err) {
    console.warn('[sessionReviewService] unexpected error:', err);
    trackEvent('failed_load', 'technical', { target: 'session_review', code: 'exception' });
    return { status: 'error', error: err?.message || String(err) };
  }
}

/** Canonical in-app link to one completed attempt. */
export function sessionReviewPath(sessionId, { source } = {}) {
  const params = new URLSearchParams({ view: 'results', session: String(sessionId) });
  if (source) params.set('from', source);
  return `/?${params.toString()}`;
}
